import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { keyframes } from "@mui/material/styles";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Menu,
  Modal,
  Slide,
  Button,
  Select,
  FormControl,
  FormHelperText,
  InputLabel,
  Skeleton,
  Alert,
  Tab,
  Tabs,
  Switch,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  Close as CloseSmallIcon,
} from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import colors from "../../../../utils/colors";
import { formatDate } from "../../../../utils/format";
import V2PageHeader from "../../../common/V2PageHeader";
import PermissionGate from "../../../guards/PermissionGate";
import axios from "../../../../utils/axios/v1/userAxios";
import useRBAC from "../../../../hooks/user/useRBAC";
import useAuth from "../../../../hooks/useAuth";
import useHome from "../../../../hooks/user/useHome";
import usePermissions from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../utils/permissions";
import { UserType, RBACPermissionCategories, normalizeUserType } from "../../../../utils/constants";
import { getUserRoleString } from "../../../../utils/format";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as ProjectIcon } from "../../../../vendor/_new/icon/navbar/project.svg";
import ProjectRoleRepeater from "./ProjectRoleRepeater";
import { ReactComponent as PlusIcon } from "../../../../vendor/_new/icon/sites/plus.svg";
import { ReactComponent as CheckIcon } from "../../../../vendor/_new/icon/sites/check.svg";
import { ReactComponent as EditIcon } from "../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../../vendor/_new/icon/sites/delete.svg";
import { ReactComponent as ProfileIcon } from "../../../../vendor/_new/icon/navbar/account/profile.svg";
import { ReactComponent as SettingsIcon } from "../../../../vendor/_new/icon/sidebar/settings.svg";
import { SafeTextField, GreenSelectForm } from "../../../common/Greens";
import { CancelButton, SaveButton } from "../../../common/Button";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../super/design";
import {
  confirmPasswordField,
  emailField,
  firstNameField,
  jobTitleField,
  lastNameField,
  newUserPasswordField,
  projectRolesField,
  projectRolesRequiredField,
  validateRoleName,
} from "../../../../utils/validation";
import { FIELD_LIMITS } from "../../../../utils/validation/yupCommonFields";
import DeleteConfirmModal from "../../../common/DeleteConfirmModal";
import { useNotification } from "../../../../contexts/NotificationContext";
import { Shield, Users as UsersIcon } from "lucide-react";
import {
  SettingsFilterBar,
  SettingsSearchField,
  SettingsFilterMenu,
  SettingsFilterRefreshButton,
  SettingsFilterSecondaryButton,
  SettingsFilterPrimaryButton,
  SettingsFilterClearButton,
} from "./UserManagementFilterBar";

const checkboxCheckBounce = keyframes`
  0% {
    transform: scale(0.75);
    opacity: 0.6;
  }
  55% {
    transform: scale(1.12);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const rolePermissionCheckboxSx = {
  p: 0.25,
  color: colors.neutral[400],
  transition: "color 0.2s ease",
  "& .MuiSvgIcon-root": {
    transition: "transform 0.15s ease, opacity 0.15s ease",
  },
  "&.Mui-checked": {
    color: colors.primary[300],
    "& .MuiSvgIcon-root": {
      animation: `${checkboxCheckBounce} 0.28s cubic-bezier(0.34, 1.4, 0.64, 1)`,
    },
  },
  "&.MuiCheckbox-indeterminate": {
    color: colors.primary[300],
    "& .MuiSvgIcon-root": {
      animation: `${checkboxCheckBounce} 0.28s cubic-bezier(0.34, 1.4, 0.64, 1)`,
    },
  },
  "&.Mui-disabled": { color: colors.neutral[300] },
};

const centeredModalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 440,
  maxWidth: "94vw",
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
  p: 3,
  outline: "none",
};

const centeredModalCancelButtonSx = {
  height: 36,
  px: 3,
  fontWeight: 500,
  textTransform: "none",
  borderRadius: "8px",
  fontSize: 14,
  borderColor: colors.neutral[300],
  color: colors.neutral[700],
  backgroundColor: "#fff",
  "&:hover": {
    borderColor: colors.neutral[400],
    backgroundColor: colors.neutral[50],
    color: colors.neutral[800],
  },
  "&.Mui-disabled": {
    borderColor: colors.neutral[300],
    color: colors.neutral[500],
    backgroundColor: colors.neutral[100],
    opacity: 1,
  },
};

const centeredInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
    lineHeight: "21px",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: colors.neutral[700],
    fontSize: 14,
    lineHeight: "21px",
  },
  "& input.MuiOutlinedInput-input": {
    paddingTop: "0px",
    paddingBottom: "0px",
    height: "auto",
  },
};

const modalSectionTitleSx =  { fontSize: 16, fontWeight: 500, color: colors.neutral[1000] };
const modalLabelSx =  { fontSize: 14, fontWeight: 500, color: colors.neutral[700], mb: 1 };
const modalHelperTextSx =  { fontSize: 12, color: colors.neutral[600] };

const tableHeadSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const tableBodySx =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] };

const getRoleChip = (role) => {
  let backgroundColor, borderColor, fontColor;
  switch (role) {
    case "Administrator":
    case "Super Administrator":
      backgroundColor = colors.primary[100];
      borderColor = colors.primary[300];
      fontColor = colors.primary[300];
      break;
    case "Organisation User":
      backgroundColor = colors.green[10];
      borderColor = colors.green[100];
      fontColor = colors.green[300];
      break;
    case "Organisation Administrator":
      backgroundColor = colors.neutral[100];
      borderColor = colors.neutral[400];
      fontColor = colors.neutral[600];
      break;
    default:
      backgroundColor = colors.neutral[100];
      borderColor = colors.neutral[400];
      fontColor = colors.neutral[600];
  }
  return (
    <Chip
      label={role}
      size="small"
      sx={{
        fontSize: 12,
        backgroundColor,
        border: `1px solid ${borderColor}`,
        color: fontColor,
      }}
    />
  );
};

const getStatusChip = (status) => {
  const palette = {
    Active: { backgroundColor: colors.green[10], borderColor: colors.green[100], color: colors.green[300] },
    Inactive: { backgroundColor: colors.neutral[50], borderColor: colors.neutral[300], color: colors.neutral[700] },
    Deleted: { backgroundColor: colors.red[10], borderColor: colors.red[100], color: colors.red[100] },
  };
  const chipStyle = palette[status] || palette.Inactive;
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontSize: 12,
        backgroundColor: chipStyle.backgroundColor,
        border: `1px solid ${chipStyle.borderColor}`,
        color: chipStyle.color,
      }}
    />
  );
};

const USER_STATUS_FILTER = {
  ACTIVE: "active",
  ALL: "all",
  DELETED: "deleted",
};

const USER_STATUS_OPTIONS = [
  { value: USER_STATUS_FILTER.ACTIVE, label: "Active users" },
  { value: USER_STATUS_FILTER.ALL, label: "All users" },
  { value: USER_STATUS_FILTER.DELETED, label: "Deleted users" },
];

function SkeletonContent({ rowsPerPage }) {
  const rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  const cellArray = [0, 1, 2, 3, 4, 5];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{ padding: "16px 8px" }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{ borderRadius: "11px" }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

SkeletonContent.propTypes = {
  rowsPerPage: PropTypes.number.isRequired,
};

const UserActionMenu = ({ user, onEdit, onDelete, canManage }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isDeleted = !!user.deleted;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="User actions"
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        disabled={isDeleted}
        sx={{ opacity: isDeleted ? 0.4 : 1 }}
      >
        <MoreVertIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.neutral[200]}`,
          },
        }}
      >
        <MenuItem
          disabled={!canManage}
          onClick={() => {
            onEdit(user);
            setAnchorEl(null);
          }}
          sx={ { fontSize: 13, color: colors.neutral[800] }}
        >
          <EditIcon style={{ width: 16, height: 16, marginRight: 8, color: colors.neutral[600] }} />
          Edit User
        </MenuItem>
        <MenuItem
          disabled={!canManage}
          onClick={() => {
            onDelete(user);
            setAnchorEl(null);
          }}
          sx={ { fontSize: 13, color: colors.red[100] }}
        >
          <DeleteIcon style={{ width: 16, height: 16, marginRight: 8 }} />
          Delete User
        </MenuItem>
      </Menu>
    </>
  );
};

UserActionMenu.propTypes = {
  user: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  canManage: PropTypes.bool.isRequired,
};

const SyncFormValues = ({ values, onChange }) => {
  useEffect(() => {
    onChange(values);
  }, [values, onChange]);
  return null;
};

/** Re-run Formik validation when the access tab (and thus schema) changes. */
const AccessTabSchemaSync = ({ accessTab, validateForm }) => {
  useEffect(() => {
    validateForm();
  }, [accessTab, validateForm]);
  return null;
};

/** Strip whitespace so space keys never land in password fields (LE-323). */
const stripPasswordWhitespace = (event) => ({
  ...event,
  target: {
    name: event.target.name,
    value: String(event.target.value ?? "").replace(/\s/g, ""),
  },
});

const ACCESS_TAB_ROLE = "role";
const ACCESS_TAB_PROJECT = "project";

const AddEditUserModal = ({
  open,
  onClose,
  user,
  onSave,
  roleOptions: availableRoleOptions,
  projects,
  saveError,
  onClearSaveError,
}) => {
  const isEdit = Boolean(user);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);
  const [formValues, setFormValues] = useState(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [accessTab, setAccessTab] = useState(ACCESS_TAB_ROLE);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: firstNameField,
        lastName: lastNameField,
        email: emailField,
        title: jobTitleField,
        roleId:
          accessTab === ACCESS_TAB_ROLE
            ? Yup.string().required("Role is required")
            : Yup.string().nullable(),
        projectRoles:
          accessTab === ACCESS_TAB_PROJECT
            ? projectRolesRequiredField
            : projectRolesField,
        ...(isEdit
          ? {}
          : {
              password: newUserPasswordField,
              confirmPassword: confirmPasswordField("password"),
            }),
      }),
    [accessTab, isEdit]
  );

  const initialValues = user
    ? {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        title: user.title || "",
        userType: user.userType || UserType.ORG_USER,
        roleId: user.roleId || "",  // Use roleId field for UUID
        // Empty [] is truthy — ensure one blank row when no assignments (same as Add).
        projectRoles:
          Array.isArray(user.projectRoles) && user.projectRoles.some((pr) => pr?.projectId && pr?.roleId)
            ? user.projectRoles
            : [{ projectId: "", roleId: "" }],
      }
    : {
        firstName: "",
        lastName: "",
        email: "",
        title: "",
        userType: UserType.ORG_USER,
        roleId: "",  // Use roleId field for UUID
        projectRoles: [{ projectId: '', roleId: '' }],
        password: "",
        confirmPassword: "",
      };


  useEffect(() => {
    if (open) {
      const hasProjectAssignment = Array.isArray(user?.projectRoles)
        && user.projectRoles.some((pr) => Boolean(pr?.projectId) && Boolean(pr?.roleId));

      // Project data → Project tab; role-only / neither / create → Role tab
      setAccessTab(hasProjectAssignment ? ACCESS_TAB_PROJECT : ACCESS_TAB_ROLE);
      setBaselineKey((k) => k + 1);
      setFormValues(initialValues);
      onClearSaveError?.();
    }
  }, [open, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const formSnapshot = useMemo(() => formValues ?? initialValues, [formValues, initialValues]);
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(onClose, isDirty, {
    disabled: isSubmittingForm,
  });


  return (
    <>
    <Modal open={open} onClose={requestClose}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}`, flexShrink: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ProfileIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
              </Box>
              <Typography sx={{ fontSize: 20, fontWeight: "medium", color: colors.neutral[1000] }}>
                {isEdit ? "Edit User" : "Add User"}
              </Typography>
            </Stack>
            <IconButton onClick={requestClose}>
              <CloseIcon style={{ width: 24, height: 24, color: colors.neutral[600] }} />
            </IconButton>
          </Stack>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            validateOnMount
            validateOnChange
            onSubmit={async (values, { setSubmitting }) => {
              setIsSubmittingForm(true);
              try {
                await onSave({ ...values, accessMode: accessTab });
                onClose();
              } catch (err) {
                // Error is handled in onSave
              } finally {
                setSubmitting(false);
                setIsSubmittingForm(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, isValid, touched, values, setFieldValue, validateForm }) => (
              <>
                <SyncFormValues values={values} onChange={setFormValues} />
                <AccessTabSchemaSync accessTab={accessTab} validateForm={validateForm} />
                <form
                  onSubmit={handleSubmit}
                  style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
                >
                  <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                    <ReactPerfectScrollbar>
                      <Box sx={{ p: 4 }}>
                        <Stack spacing={4}>
                          <Box
                            sx={{
                              backgroundColor: "#fff",
                              borderRadius: "12px",
                              p: 3,
                              border: `1px solid ${colors.neutral[200]}`,
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
                              <ProfileIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                              <Typography sx={modalSectionTitleSx}>
                                User Information
                              </Typography>
                            </Stack>

                        <Stack spacing={3}>
                          <SafeTextField
                            fullWidth
                            name="firstName"
                            label="First name"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(touched.firstName && errors.firstName)}
                            helperText={(touched.firstName && errors.firstName) || ""}
                            inputProps={{ maxLength: FIELD_LIMITS.personName }}
                          />

                          <SafeTextField
                            fullWidth
                            name="lastName"
                            label="Last name"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(touched.lastName && errors.lastName)}
                            helperText={(touched.lastName && errors.lastName) || ""}
                            inputProps={{ maxLength: FIELD_LIMITS.personName }}
                          />

                          <SafeTextField
                            fullWidth
                            name="title"
                            label="Title"
                            value={values.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(touched.title && errors.title)}
                            helperText={(touched.title && errors.title) || ""}
                            inputProps={{ maxLength: FIELD_LIMITS.personName }}
                          />

                          <SafeTextField
                            fullWidth
                            name="email"
                            type="email"
                            label="Email address"
                            value={values.email}
                            onChange={(e) => {
                              handleChange(e);
                              if (saveError) onClearSaveError?.();
                            }}
                            onBlur={handleBlur}
                            error={Boolean((touched.email && errors.email) || saveError)}
                            helperText={
                              (touched.email && errors.email)
                              || saveError
                              || (isEdit ? "Email cannot be changed after user creation" : "")
                            }
                            disabled={isEdit}
                            inputProps={{ maxLength: FIELD_LIMITS.email }}
                          />

                          {!isEdit && (
                            <>
                              <SafeTextField
                                fullWidth
                                name="password"
                                type={showPassword ? "text" : "password"}
                                label="Password"
                                sanitize={false}
                                value={values.password}
                                onChange={(e) => handleChange(stripPasswordWhitespace(e))}
                                onBlur={handleBlur}
                                error={Boolean(touched.password && errors.password)}
                                helperText={(touched.password && errors.password) || ""}
                                inputProps={{ maxLength: FIELD_LIMITS.passwordMax }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                      >
                                        {showPassword ? (
                                          <VisibilityOffIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                                        ) : (
                                          <VisibilityIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />

                              <SafeTextField
                                fullWidth
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                label="Confirm password"
                                sanitize={false}
                                value={values.confirmPassword}
                                onChange={(e) => handleChange(stripPasswordWhitespace(e))}
                                onBlur={handleBlur}
                                error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                                helperText={(touched.confirmPassword && errors.confirmPassword) || ""}
                                inputProps={{ maxLength: FIELD_LIMITS.passwordMax }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                        size="small"
                                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                      >
                                        {showConfirmPassword ? (
                                          <VisibilityOffIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                                        ) : (
                                          <VisibilityIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </>
                          )}
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "12px",
                          p: 3,
                          border: `1px solid ${colors.neutral[200]}`,
                        }}
                      >
                        <Stack spacing={3}>
                          <Box>
                            <GreenSelectForm fullWidth variant="outlined">
                              <InputLabel id="user-scope-label" shrink>
                                Scope
                              </InputLabel>
                              <Select
                                labelId="user-scope-label"
                                label="Scope"
                                value={accessTab}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setAccessTab(next);
                                  // Exclusive mode: clear the other side in the form only.
                                  // Cancel closes without save, so original data remains on reopen.
                                  if (next === ACCESS_TAB_PROJECT) {
                                    setFieldValue("roleId", "");
                                  } else {
                                    setFieldValue("projectRoles", [{ projectId: "", roleId: "" }]);
                                  }
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      "& .MuiMenuItem-root": { fontSize: 14 },
                                      "& .MuiMenuItem-root .MuiTypography-root": { fontSize: 14 },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value={ACCESS_TAB_ROLE}>
                                  <Typography sx={{ fontSize: 14, color: colors.neutral[800] }}>
                                    Global
                                  </Typography>
                                </MenuItem>
                                <MenuItem value={ACCESS_TAB_PROJECT}>
                                  <Typography sx={{ fontSize: 14, color: colors.neutral[800] }}>
                                    Project Based
                                  </Typography>
                                </MenuItem>
                              </Select>
                            </GreenSelectForm>
                          </Box>

                          {accessTab === ACCESS_TAB_ROLE && (
                            <Stack spacing={3}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <SettingsIcon style={{ width: 24, height: 24, color: "#6371F7" }} />
                                <Typography sx={modalSectionTitleSx}>
                                  Role
                                </Typography>
                              </Stack>
                              <Box>
                                <GreenSelectForm
                                  fullWidth
                                  variant="outlined"
                                  error={Boolean(touched.roleId && errors.roleId)}
                                >
                                  <InputLabel id="user-role-label" shrink>
                                    Role
                                  </InputLabel>
                                  <Select
                                    labelId="user-role-label"
                                    label="Role"
                                    name="roleId"
                                    value={values.roleId || ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    displayEmpty
                                    renderValue={(selected) => {
                                      if (!selected) {
                                        return (
                                          <Typography component="span" sx={{ fontSize: 14, color: colors.neutral[400] }}>
                                            Select a role
                                          </Typography>
                                        );
                                      }
                                      const option = availableRoleOptions.find((o) => o.value === selected);
                                      return (
                                        <Typography component="span" sx={{ fontSize: 14, color: colors.neutral[800] }}>
                                          {option?.label || selected}
                                        </Typography>
                                      );
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          "& .MuiMenuItem-root": { fontSize: 14 },
                                          "& .MuiMenuItem-root .MuiTypography-root": { fontSize: 14 },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      <Typography sx={{ fontSize: 14, color: colors.neutral[400] }}>
                                        Select a role
                                      </Typography>
                                    </MenuItem>
                                    {availableRoleOptions.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <Typography sx={{ fontSize: 14, color: colors.neutral[800] }}>
                                            {option.label}
                                          </Typography>
                                          {option.isCustom && (
                                            <Box
                                              component="span"
                                              sx={{
                                                px: 1.5,
                                                py: 0.25,
                                                borderRadius: "4px",
                                                backgroundColor: "#EEF0FE",
                                                color: "#6371F7",
                                                fontSize: 12,
                                                fontWeight: 500,
                                              }}
                                            >
                                              Custom
                                            </Box>
                                          )}
                                        </Stack>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </GreenSelectForm>
                                {touched.roleId && errors.roleId ? (
                                  <FormHelperText error>{errors.roleId}</FormHelperText>
                                ) : null}
                              </Box>

                              {values.roleId && (
                                <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800], mb: 1 }}>
                                    Role Permissions
                                  </Typography>
                                  <Typography sx={modalHelperTextSx}>
                                    {(() => {
                                      const selectedRole = availableRoleOptions.find((r) => r.value === values.roleId);
                                      return selectedRole?.description || "This role has custom permissions configured by your organization.";
                                    })()}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          )}

                          {accessTab === ACCESS_TAB_PROJECT && (
                            <Stack spacing={3}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <ProjectIcon style={{ width: 24, height: 24, color: "#6371F7" }} />
                                <Typography sx={modalSectionTitleSx}>
                                  Project Assignment
                                </Typography>
                              </Stack>
                              <ProjectRoleRepeater
                                projectRoles={values.projectRoles}
                                onChange={(newProjectRoles) => setFieldValue("projectRoles", newProjectRoles)}
                                projects={projects}
                                roles={availableRoleOptions}
                                errors={errors.projectRoles}
                                touched={Boolean(touched.projectRoles)}
                                helperText="Assign roles to projects for this user. At least one project assignment is required for this option."
                              />
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                        </Stack>
                      </Box>
                    </ReactPerfectScrollbar>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      flexShrink: 0,
                      px: 4,
                      py: 3,
                      borderTop: `1px solid ${colors.neutral[200]}`,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || !isValid}
                      startIcon={<CheckIcon style={{ width: 16, height: 16, color: "#fff" }} />}
                      sx={{
                        height: 44,
                        borderRadius: "8px",
                        backgroundColor: "#6371F7",
                        border: "1px solid #6371F7",
                        color: "#fff",
                        fontSize: 14,
                        textTransform: "none",
                        px: 4,
                        "&:hover": { backgroundColor: "#4959ee", borderColor: "#4959ee", color: "#fff" },
                      }}
                    >
                      {isEdit ? "Save Changes" : "Add User"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={requestClose}
                      startIcon={<CloseIcon style={{ width: 14, height: 14, color: colors.neutral[700] }} />}
                      sx={{
                        height: 44,
                        borderRadius: "8px",
                        borderColor: colors.neutral[300],
                        color: colors.neutral[700],
                        fontSize: 14,
                        textTransform: "none",
                        px: 3,
                        "&:hover": { borderColor: colors.neutral[400], backgroundColor: "transparent", color: colors.neutral[900], fontWeight: 500 },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </form>
              </>
            )}
          </Formik>
        </Box>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};

AddEditUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  roleOptions: PropTypes.array.isRequired,
  projects: PropTypes.array.isRequired,
  saveError: PropTypes.string,
  onClearSaveError: PropTypes.func,
};

const ToggleUserStatusModal = ({ open, onClose, user, onConfirm }) => {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
    }
  }, [open]);

  if (!user) return null;

  const nextEnabled = !user.enabled;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          backgroundColor: "#fff",
          borderRadius: "8px",
          p: 4,
          boxShadow: 24,
        }}
      >
        <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000], mb: 2 }}>
          {nextEnabled ? "Enable User?" : "Disable User?"}
        </Typography>
        <Typography sx={ { fontSize: 14, color: colors.neutral[700], mb: 4 }}>
          Are you sure you want to {nextEnabled ? "enable" : "disable"} <strong>{user.firstName} {user.lastName}</strong> ({user.email})?
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              height: 36,
              borderRadius: "8px",
              borderColor: colors.neutral[300],
              color: colors.neutral[700],
              fontSize: 14,
              fontWeight: 500,
              textTransform: "none",
              backgroundColor: "#fff",
              "&:hover": { borderColor: colors.neutral[400], backgroundColor: colors.neutral[50], color: colors.neutral[800] },
              "&.Mui-disabled": {
                borderColor: colors.neutral[300],
                color: colors.neutral[500],
                backgroundColor: colors.neutral[100],
                opacity: 1,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true);
              await onConfirm(user);
              onClose();
              setSubmitting(false);
            }}
            sx={{
              height: 36,
              borderRadius: "8px",
              border: "1px solid",
              borderColor: nextEnabled ? colors.primary[300] : colors.red[100],
              backgroundColor: nextEnabled ? colors.primary[300] : colors.red[100],
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: nextEnabled ? "#4959ee" : "#b42318",
                borderColor: nextEnabled ? "#4959ee" : "#b42318",
                color: "#fff",
              },
              "&.Mui-disabled": {
                borderColor: nextEnabled ? colors.primary[300] : colors.red[100],
                backgroundColor: nextEnabled ? colors.primary[300] : colors.red[100],
                color: "#fff",
                opacity: 0.7,
              },
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />
                {nextEnabled ? "Enabling..." : "Disabling..."}
              </>
            ) : nextEnabled ? (
              "Enable"
            ) : (
              "Disable"
            )}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

ToggleUserStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
};

const getRoleType = (role) => {
  if (role?.type === "default" || role?.type === "custom") {
    return role.type;
  }
  return role?.isSystem ? "default" : "custom";
};

const isCustomRole = (role) => getRoleType(role) === "custom";

const getRoleTypeChip = (role) => {
  const roleType = getRoleType(role);
  if (roleType === "default") {
    return (
      <Chip
        label="Default"
        size="small"
        sx={{
          fontSize: 12,
          backgroundColor: colors.primary[100],
          border: `1px solid ${colors.primary[300]}`,
          color: colors.primary[300],
        }}
      />
    );
  }
  return (
    <Chip
      label="Custom"
      size="small"
      sx={{
        fontSize: 12,
        backgroundColor: colors.green[10],
        border: `1px solid ${colors.green[100]}`,
        color: colors.green[300],
      }}
    />
  );
};

const RoleModal = ({ open, onClose, role, onSave, loading, saveError, onClearSaveError }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  const nameValidation = useMemo(() => validateRoleName(name), [name]);
  const canSubmit = nameValidation.valid && permissions.length > 0;

  useEffect(() => {
    if (role) {
      setName(role.name || "");
      setDescription(role.description || "");
      setPermissions(role.permissions || []);
    } else {
      setName("");
      setDescription("");
      setPermissions([]);
    }
    setErrors({});
    setSubmitAttempted(false);
    if (open) setBaselineKey((k) => k + 1);
  }, [role, open]);

  const formSnapshot = useMemo(
    () => ({ name, description, permissions }),
    [name, description, permissions],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(onClose, isDirty, { disabled: loading });

  const handlePermissionToggle = (permission) => {
    setPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission]
    );
  };

  const handleCategoryToggle = (categoryPerms) => {
    const allSelected = categoryPerms.every((p) => permissions.includes(p));
    if (allSelected) {
      setPermissions((prev) => prev.filter((p) => !categoryPerms.includes(p)));
    } else {
      setPermissions((prev) => [...new Set([...prev, ...categoryPerms])]);
    }
  };

  const handleSubmit = () => {
    setSubmitAttempted(true);
    const newErrors = {};
    if (!nameValidation.valid) newErrors.name = nameValidation.message;
    if (permissions.length === 0) newErrors.permissions = "At least one permission is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({ name: name.trim(), description: description.trim(), permissions });
  };

  const showNameErr = (submitAttempted && !nameValidation.valid)
    ? nameValidation.message
    : (saveError || errors.name);

  const isEditMode = !!role?.id;

  return (
    <>
    <Modal open={open} onClose={requestClose}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "50%",
            minWidth: 600,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={4} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SettingsIcon style={{ width: 24, height: 24, color: "#6371F7" }} />
              </Box>
              <Stack>
                <Typography sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}>
                  {isEditMode ? "Edit Role" : "Create Role"}
                </Typography>
                <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  {isEditMode ? "Modify role permissions" : "Define a new custom role"}
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon style={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, px: 4, pb: 2, overflow: "hidden" }}>
            <ReactPerfectScrollbar>
              <Stack spacing={3} sx={{ pr: 2 }}>
                <SafeTextField
                  label="Role Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (saveError) onClearSaveError?.();
                  }}
                  error={Boolean(showNameErr)}
                  helperText={showNameErr}
                  fullWidth
                  disabled={loading}
                  inputProps={{ maxLength: FIELD_LIMITS.roleName }}
                />

                <SafeTextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading}
                />

                <Stack spacing={2}>
                  <Typography sx={ { fontWeight: "medium", fontSize: 16, color: colors.neutral[1000] }}>
                    Permissions
                  </Typography>
                  {errors.permissions && (
                    <Alert severity="error" sx={{ }}>{errors.permissions}</Alert>
                  )}

                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
                    {Object.entries(RBACPermissionCategories).map(([key, category]) => (
                      <Box
                        key={key}
                        sx={{
                          p: 2,
                          borderRadius: "8px",
                          border: `1px solid ${colors.neutral[300]}`,
                          backgroundColor: colors.neutral[50],
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Checkbox
                            size="small"
                            checked={category.permissions.every((p) => permissions.includes(p))}
                            indeterminate={
                              category.permissions.some((p) => permissions.includes(p)) &&
                              !category.permissions.every((p) => permissions.includes(p))
                            }
                            onChange={() => handleCategoryToggle(category.permissions)}
                            disabled={loading}
                            sx={rolePermissionCheckboxSx}
                          />
                          <Typography sx={ { fontWeight: "medium", fontSize: 14, color: colors.neutral[1000], cursor: "pointer" }} onClick={() => !loading && handleCategoryToggle(category.permissions)}>
                            {category.label}
                          </Typography>
                        </Stack>
                        <Stack sx={{ ml: 2.5 }} spacing={0.5}>
                          {category.permissions.map((perm) => (
                            <Stack direction="row" alignItems="center" spacing={1} key={perm} sx={{ py: 0.25 }}>
                              <Checkbox
                                size="small"
                                checked={permissions.includes(perm)}
                                onChange={() => handlePermissionToggle(perm)}
                                disabled={loading}
                                sx={rolePermissionCheckboxSx}
                              />
                              <Typography
                                sx={ { fontSize: 12, color: colors.neutral[700], cursor: "pointer" }}
                                onClick={() => !loading && handlePermissionToggle(perm)}
                              >
                                {perm}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>

          <Stack direction="row" spacing={2} sx={{ p: 4, borderTop: `1px solid ${colors.neutral[200]}` }}>
            <CancelButton
              onClick={requestClose}
              disabled={loading}
              sx={{ width: "auto", px: 3 }}
            />
            <SaveButton
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
              label={loading ? "Saving..." : isEditMode ? "Save Changes" : "Create Role"}
              sx={{ width: "auto", px: 3 }}
            />
          </Stack>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};

const CloneRoleModal = ({ open, onClose, role, onClone, loading }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const nameValidation = useMemo(() => validateRoleName(name), [name]);

  useEffect(() => {
    if (role) {
      setName(`${role.name} (Copy)`);
    }
    setError("");
    setSubmitAttempted(false);
  }, [role, open]);

  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (!nameValidation.valid) {
      setError(nameValidation.message);
      return;
    }
    onClone(name.trim());
  };

  const showError = (submitAttempted && !nameValidation.valid) ? nameValidation.message : error;

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        },
      }}
    >
      <Box sx={centeredModalBoxSx}>
        <Stack spacing={0.5} sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500, lineHeight: "27px", color: colors.neutral[1000] }}>
            Clone Role
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Create a copy of{" "}
            <Box component="span" sx={{ fontWeight: 500, color: colors.neutral[800] }}>
              {role?.name}
            </Box>{" "}
            with a new name
          </Typography>
        </Stack>
        <Box sx={{ mb: 3 }}>
          <Typography sx={modalLabelSx}>New Role Name</Typography>
          <SafeTextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={Boolean(showError)}
            helperText={showError}
            fullWidth
            size="small"
            placeholder="Enter role name"
            disabled={loading}
            inputProps={{ maxLength: FIELD_LIMITS.roleName }}
            sx={{ ...centeredInputSx, backgroundColor: "#fff" }}
          />
        </Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={centeredModalCancelButtonSx}
          >
            Cancel
          </Button>
          <SaveButton
            onClick={handleSubmit}
            disabled={loading || !nameValidation.valid}
            loading={loading}
            label={loading ? "Cloning..." : "Clone"}
            sx={{ width: "auto", px: 3, height: 36, fontWeight: 600 }}
          />
        </Stack>
      </Box>
    </Modal>
  );
};

const DeleteRoleModal = ({ open, onClose, role, onDelete, loading }) => (
  <Modal open={open} onClose={loading ? undefined : onClose}>
    <Stack
      spacing={4}
      sx={{
        position: "absolute",
        top: "30%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 450,
        maxWidth: "94vw",
        backgroundColor: colors.neutral[100],
        borderRadius: "8px",
        p: 6,
        outline: "none",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
        <Typography sx={{ fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
          Delete Role?
        </Typography>
        <Box
          onClick={loading ? undefined : onClose}
          sx={{ cursor: loading ? "default" : "pointer", lineHeight: 0 }}
          aria-hidden
        >
          <CloseIcon style={{ color: colors.neutral[600] }} />
        </Box>
      </Stack>
      <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
        You&apos;re about to permanently delete the role{" "}
        <Box component="span" sx={{ color: colors.neutral[1000], fontWeight: 700 }}>
          {role?.name}
        </Box>
        .
      </Typography>
      <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
        This action cannot be undone.
      </Typography>
      <Stack direction="row" alignItems="center" spacing={4}>
        <CancelButton onClick={onClose} outlined disabled={loading} />
        <SaveButton onClick={onDelete} disabled={loading} loading={loading} label="Delete" />
      </Stack>
    </Stack>
  </Modal>
);

const UserManagement = () => {
  const { userType } = useAuth();
  const { curProjectId } = useHome();
  const isOrgAdmin = userType === UserType.ORG_ADMIN;
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, hasRolePermission } = usePermissions();

  const canManageUsers = hasRolePermission(PERMISSIONS.USERS_MANAGE);
  
  const [selectedTab, setSelectedTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(USER_STATUS_FILTER.ACTIVE);
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [toggleStatusModalOpen, setToggleStatusModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [projects, setProjects] = useState([]);
  
  const {
    roles,
    loading: rolesLoading,
    getRoles,
    createRole,
    updateRole,
    deleteRole,
    cloneRole,
    clearError,
  } = useRBAC();
  
  const { showSuccess, showError } = useNotification();
  const { getAllProjectBaseList, projectList } = useHome();
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleSaveError, setRoleSaveError] = useState("");
  const [userSaveError, setUserSaveError] = useState("");
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [deleteRoleModalOpen, setDeleteRoleModalOpen] = useState(false);

  // Map API response to UI format
  const mapUser = useCallback((user) => {
    // Map projectRoles from API response
    // API returns 'projects' array with projectId and roleId
    let mappedProjectRoles = [];
    
    if (user.projects && Array.isArray(user.projects)) {
      // Map from API format: { projectId, roleId, projectName, roleName }
      mappedProjectRoles = user.projects.map(p => ({
        projectId: p.projectId,
        roleId: p.roleId
      }));
    } else if (user.projectRoles && Array.isArray(user.projectRoles)) {
      // Fallback if API returns projectRoles directly
      mappedProjectRoles = user.projectRoles;
    }
    
    return {
      id: user.uid || user.id || user._id,
      firstName: user.first_name || user.firstName || user.name?.split(" ")[0] || "",
      lastName: user.last_name || user.lastName || user.name?.split(" ").slice(1).join(" ") || "",
      email: user.email || "",
      userType: normalizeUserType(user.userType),
      userTypeLabel: getUserRoleString(user.userType),
      roleName: user.role?.name || user.roleName || "",
      roleId: user.roleId || user.role_id || "",  // Map the roleId field (UUID)
      status: user.enabled === false ? "Inactive" : user.isDeleted || user.deleted ? "Deleted" : "Active",
      enabled: user.enabled !== false,
      deleted: !!(user.isDeleted || user.deleted),
      lastLogin: user.last_login || user.lastLogin || null,
      createdAt: user.created_at || user.createdAt || user.created || null,
      title: user.title || "",
      verified: user.verified || null,
      projectRoles: mappedProjectRoles,
    };
  }, []);

  // Fetch users from API
  const fetchUsers = useCallback(async (refresh = true) => {
    setLoading(refresh);
    setError(null);
    try {
      const params = {};
      if (curProjectId && curProjectId !== "all") {
        params.project_id = curProjectId;
      }
      params.include_deleted = true;
      const response = await axios.get("/users", { params });
      const mappedUsers = (response.data || []).map(mapUser);
      setUsers(mappedUsers);
    } catch (err) {
      setError(err.message || "Failed to fetch users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [mapUser, curProjectId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        !searchQuery ||
        user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = !roleFilter || user.roleId === roleFilter;
      const matchesStatus =
        statusFilter === USER_STATUS_FILTER.ALL ||
        (statusFilter === USER_STATUS_FILTER.ACTIVE && !user.deleted) ||
        (statusFilter === USER_STATUS_FILTER.DELETED && user.deleted);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  const hasUserFilters = Boolean(
    searchQuery || roleFilter || statusFilter !== USER_STATUS_FILTER.ACTIVE
  );

  const handleClearUserFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setStatusFilter(USER_STATUS_FILTER.ACTIVE);
  };

  // Build role options from API response (uses type field: "default" or "custom")
  const allRoleOptions = useMemo(() => {
    const seenRoles = new Set();
    
    return (roles || [])
      .filter((role) => {
        // Skip duplicates based on role ID
        const roleId = role.id || role._id || role.uid;
        if (seenRoles.has(roleId)) {
          return false;
        }
        seenRoles.add(roleId);
        return true;
      })
      .map((role) => ({
        value: role.id || role._id || role.uid,  // Use role ID as value
        label: role.name,
        description: role.description || "",
        isCustom: isCustomRole(role),
        id: role.id || role._id || role.uid,  // Also store id for reference
      }));
  }, [roles]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setUserSaveError("");
    setAddEditModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserSaveError("");
    setAddEditModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleSaveUser = async (userData) => {
    setUserSaveError("");
    try {
      const accessMode = userData.accessMode || ACCESS_TAB_ROLE;
      const validProjectRoles = (userData.projectRoles || []).filter(
        (pr) => pr.projectId && pr.roleId
      );

      // Exclusive save: keep selected mode data, clear the other side.
      const roleId =
        accessMode === ACCESS_TAB_ROLE ? (userData.roleId || null) : null;
      const projectRoles =
        accessMode === ACCESS_TAB_PROJECT ? validProjectRoles : [];

      if (selectedUser) {
        const apiData = {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          title: userData.title || "",
          userType: userData.userType || selectedUser?.userType || UserType.ORG_USER,
          roleId,
          projectRoles,
        };
        await axios.put(`/users/${selectedUser.id}`, apiData);
        showSnack("User updated successfully", "success");
      } else {
        const apiData = {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          title: userData.title || "",
          password: userData.password,
          confirmPassword: userData.confirmPassword,
          userType: userData.userType || UserType.ORG_USER,
          ...(roleId ? { roleId } : {}),
          projectRoles,
          verify: false,
        };
        await axios.post("/users", apiData);
        showSnack("User created successfully", "success");
      }
      await fetchUsers();
      return true;
    } catch (err) {
      setUserSaveError(err.message || "Failed to save user");
      throw err;
    }
  };

  const handleConfirmDelete = async (user) => {
    setError(null);
    try {
      await axios.patch("/users", { uid: user.id, deleted: true });
      showSnack("User deleted successfully", "success");
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to delete user");
    }
  };

  const handleToggleEnabled = async (user) => {
    setError(null);
    try {
      await axios.patch("/users", { uid: user.id, enabled: !user.enabled });
      await fetchUsers(false);
    } catch (err) {
      setError(err.message || "Failed to update user status");
    }
  };

  const handleToggleUserClick = (user) => {
    setSelectedUser(user);
    setToggleStatusModalOpen(true);
  };

  // Fetch roles on mount for both tabs (needed for user role dropdown)
  useEffect(() => {
    getRoles().catch(() => {
      // API may not be available yet, handle gracefully
    });
  }, [getRoles]);

  // Fetch projects on mount
  useEffect(() => {
    getAllProjectBaseList().catch(() => {
      // API may not be available yet, handle gracefully
    });
  }, [getAllProjectBaseList]);

  // Update projects when projectList changes
  useEffect(() => {
    if (projectList) {
      setProjects(projectList);
    }
  }, [projectList]);

  const filteredRoles = useMemo(() => {
    return (roles || []).filter((role) =>
      role.name?.toLowerCase().includes(roleSearchQuery.toLowerCase()) ||
      role.description?.toLowerCase().includes(roleSearchQuery.toLowerCase())
    );
  }, [roles, roleSearchQuery]);

  const hasRoleFilters = Boolean(roleSearchQuery);

  const handleClearRoleFilters = () => {
    setRoleSearchQuery("");
  };

  useEffect(() => {
    const tabFromQuery = searchParams.get("tab");
    if (tabFromQuery === "roles" && isOrgAdmin) {
      setSelectedTab("roles");
    } else {
      setSelectedTab("users");
    }
  }, [searchParams, isOrgAdmin]);

  const handleTabChange = (_, tabValue) => {
    setSelectedTab(tabValue);
    const next = new URLSearchParams(searchParams);
    if (tabValue === "roles") {
      next.set("tab", "roles");
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  };

  const handleRoleMenuOpen = (event, role) => {
    setRoleAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleRoleMenuClose = () => {
    setRoleAnchorEl(null);
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setRoleSaveError("");
    clearError();
    setRoleModalOpen(true);
  };

  const handleEditRole = () => {
    handleRoleMenuClose();
    setRoleSaveError("");
    clearError();
    setRoleModalOpen(true);
  };

  const handleCloneRole = () => {
    handleRoleMenuClose();
    setCloneModalOpen(true);
  };

  const handleDeleteRoleClick = () => {
    handleRoleMenuClose();
    setDeleteRoleModalOpen(true);
  };

  const showSnack = (message, severity = "success") => {
    if (severity === "error") {
      showError(message);
    } else {
      showSuccess(message);
    }
  };

  const handleSaveRole = async (roleData) => {
    try {
      setRoleSaveError("");
      clearError();
      if (selectedRole?.id) {
        await updateRole(selectedRole.id, roleData);
        showSnack("Role updated successfully", "success");
      } else {
        await createRole(roleData);
        showSnack("Role created successfully", "success");
      }
      setRoleModalOpen(false);
    } catch (err) {
      const message = err.message || "Failed to save role";
      setRoleSaveError(message);
      clearError();
    }
  };

  const handleCloneSubmit = async (name) => {
    try {
      await cloneRole(selectedRole.id, name);
      showSnack("Role cloned successfully", "success");
      setCloneModalOpen(false);
    } catch (err) {
      showSnack(err.message || "Failed to clone role", "error");
    }
  };

  const handleDeleteRoleSubmit = async () => {
    try {
      await deleteRole(selectedRole.id);
      showSnack("Role deleted successfully", "success");
      setDeleteRoleModalOpen(false);
    } catch (err) {
      showSnack(err.message || "Failed to delete role", "error");
    }
  };

  return (
    <PermissionGate permission={PERMISSIONS.USERS_READ} resourceLabel="user management">
    <>
      <Helmet>
        <title>Access Management | Layerd AI WAF</title>
      </Helmet>
      <Box sx={{ width: "100%", height: "100%", p: 6 }}>
        <Stack spacing={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <V2PageHeader
                title="Access Management"
                description="Manage users, roles, and access permissions for the platform"
                sx={{ pb: 0 }}
              />
            </Box>
          </Stack>

          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 32,
              height: 32,
              "& .MuiTabs-flexContainer": { border: "none", px: 0, py: 0, gap: "24px", height: 32 },
              "& .MuiTabs-indicator": { height: "2px", backgroundColor: "#6371F7" },
              "& button": { p: 0, minWidth: "inherit", minHeight: "inherit" },
              "& button .MuiTypography-root": { color: colors.neutral[700] },
              "& button.Mui-selected .MuiTypography-root": { color: "#6371F7", fontWeight: "medium" },
            }}
          >
            <Tab value={"users"} label={<Typography sx={ { fontSize: 14 }}>Users</Typography>} />
            {isOrgAdmin && (
              <Tab value={"roles"} label={<Typography sx={ { fontSize: 14 }}>Roles</Typography>} />
            )}
          </Tabs>

          {selectedTab === "users" && (
            <>
              <SettingsFilterBar
                actions={(
                  <>
                    <SettingsFilterRefreshButton
                      onClick={fetchUsers}
                      disabled={loading}
                      loading={loading}
                    />
                    <SettingsFilterSecondaryButton
                      startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                      disabled={loading || users.length === 0}
                      onClick={() => {
                        if (!filteredUsers || filteredUsers.length === 0) return;
                        const headers = ["Email", "Name", "Role", "Title", "Creation Date", "Verified", "Enabled"];
                        const rows = filteredUsers.map((user) => [
                          user.email || "",
                          `${user.firstName || ""} ${user.lastName || ""}`.trim(),
                          user.roleName || "",
                          user.title || "",
                          user.createdAt ? formatDate(user.createdAt) || "Never" : "",
                          user.verified ? "Yes" : "No",
                          user.enabled ? "Yes" : "No",
                        ]);
                        const csvContent = [headers, ...rows]
                          .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                          .join("\n");
                        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(blob);
                        link.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
                        link.click();
                        URL.revokeObjectURL(link.href);
                      }}
                    >
                      Download
                    </SettingsFilterSecondaryButton>
                    <SettingsFilterPrimaryButton
                      startIcon={<PlusIcon style={{ color: "#fff" }} />}
                      onClick={handleAddUser}
                      disabled={loading || !canManageUsers}
                    >
                      Add New User
                    </SettingsFilterPrimaryButton>
                  </>
                )}
              >
                <SettingsSearchField
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users..."
                />
                <SettingsFilterMenu
                  label="Role"
                  value={roleFilter}
                  options={allRoleOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                    secondary: option.isCustom ? "Custom" : undefined,
                  }))}
                  onChange={setRoleFilter}
                  onClear={() => setRoleFilter("")}
                  icon={Shield}
                  minWidth={180}
                />
                <SettingsFilterMenu
                  label="Status"
                  value={statusFilter}
                  options={USER_STATUS_OPTIONS}
                  onChange={setStatusFilter}
                  onClear={
                    statusFilter !== USER_STATUS_FILTER.ACTIVE
                      ? () => setStatusFilter(USER_STATUS_FILTER.ACTIVE)
                      : undefined
                  }
                  icon={UsersIcon}
                  minWidth={180}
                />
                {hasUserFilters ? (
                  <SettingsFilterClearButton onClick={handleClearUserFilters} />
                ) : null}
              </SettingsFilterBar>

              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ }}>
                  {error}
                </Alert>
              )}

              <Box
                sx={{
                  border: `1px solid ${colors.neutral[200]}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                      <TableCell sx={tableHeadSx}>Email</TableCell>
                      <TableCell sx={tableHeadSx}>Name</TableCell>
                      <TableCell sx={tableHeadSx}>Role</TableCell>
                      <TableCell sx={tableHeadSx}>Title</TableCell>
                      <TableCell sx={tableHeadSx}>Creation Date</TableCell>
                      <TableCell sx={tableHeadSx}>Verified</TableCell>
                      <TableCell sx={tableHeadSx}>Enabled</TableCell>
                      <TableCell sx={tableHeadSx}>Status</TableCell>
                      <TableCell sx={{ ...tableHeadSx, width: 60 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  {loading ? (
                    <SkeletonContent rowsPerPage={5} />
                  ) : (
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ textAlign: "center", py: 6 }}>
                            <PersonIcon sx={{ fontSize: 48, color: colors.neutral[300], mb: 2 }} />
                            <Typography sx={ { fontSize: "14px !important", color: colors.neutral[600] }}>
                              No users found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map((user) => (
                          <TableRow
                            key={user.id}
                            sx={{
                              opacity: user.deleted ? 0.72 : 1,
                              "&:hover": { backgroundColor: colors.neutral[25] },
                            }}
                          >
                            <TableCell sx={tableBodySx}>{user.email || "—"}</TableCell>
                            <TableCell sx={tableBodySx}>
                              {[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}
                            </TableCell>
                            <TableCell sx={tableBodySx}>{user.roleName || "—"}</TableCell>
                            <TableCell sx={tableBodySx}>{user.title || "—"}</TableCell>
                            <TableCell sx={tableBodySx}>{formatDate(user.createdAt) || "Never"}</TableCell>
                            <TableCell sx={tableBodySx}>
                            {user.verified ? (
                                <CheckIcon sx={{ color: colors.green[300], fontSize: 20 }} />
                              ) : (
                                <CloseSmallIcon sx={{ color: colors.red[100], fontSize: 18 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={!!user.enabled}
                                onChange={() => handleToggleUserClick(user)}
                                disabled={!canManageUsers || user.deleted}
                                size="small"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: colors.primary[300],
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: colors.primary[300],
                                    opacity: 0.35,
                                  },
                                  "& .MuiSwitch-track": {
                                    backgroundColor: colors.neutral[400],
                                    opacity: 0.35,
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>{getStatusChip(user.status)}</TableCell>
                            <TableCell>
                              <UserActionMenu
                                user={user}
                                onEdit={handleEditUser}
                                onDelete={handleDeleteUser}
                                canManage={canManageUsers}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  )}
                </Table>
              </Box>
            </>
          )}

          {selectedTab === "roles" && isOrgAdmin && (
            <>
              <SettingsFilterBar
                actions={(
                  <>
                    <SettingsFilterRefreshButton
                      onClick={() => getRoles()}
                      disabled={rolesLoading}
                      loading={rolesLoading}
                    />
                    <SettingsFilterPrimaryButton
                      startIcon={<PlusIcon style={{ color: "#fff" }} />}
                      onClick={handleCreateRole}
                      disabled={!canManageUsers}
                    >
                      Create Custom Role
                    </SettingsFilterPrimaryButton>
                  </>
                )}
              >
                <SettingsSearchField
                  value={roleSearchQuery}
                  onChange={(e) => setRoleSearchQuery(e.target.value)}
                  placeholder="Search roles..."
                />
                {hasRoleFilters ? (
                  <SettingsFilterClearButton onClick={handleClearRoleFilters} />
                ) : null}
              </SettingsFilterBar>


              <Box sx={{ borderRadius: "12px", border: `1px solid ${colors.neutral[200]}`, overflow: "hidden" }}>
                <Table>
                  <TableHead sx={{ backgroundColor: colors.neutral[50] }}>
                    <TableRow>
                      <TableCell sx={tableHeadSx}>Role Name</TableCell>
                      <TableCell sx={tableHeadSx}>Description</TableCell>
                      <TableCell sx={tableHeadSx}>Type</TableCell>
                      <TableCell sx={tableHeadSx}>Created At</TableCell>
                      <TableCell sx={tableHeadSx}>Permissions</TableCell>
                      <TableCell sx={{ ...tableHeadSx, width: 60 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rolesLoading && roles.length === 0 ? (
                      [...Array(4)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton width={80} /></TableCell>
                          <TableCell><Skeleton width={120} /></TableCell>
                          <TableCell><Skeleton width={60} /></TableCell>
                          <TableCell><Skeleton width={40} /></TableCell>
                        </TableRow>
                      ))
                    ) : filteredRoles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <SettingsIcon style={{ width: 48, height: 48, color: colors.neutral[300], marginBottom: 16 }} />
                          <Typography sx={ { fontSize: 16, color: colors.neutral[600] }}>
                            No roles found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRoles.map((role) => (
                        <TableRow key={role.id} sx={{ "&:hover": { backgroundColor: colors.neutral[25] } }}>
                          <TableCell sx={tableBodySx}>
                            <Typography sx={{ fontWeight: "medium", fontSize: 14 }}>{role.name}</Typography>
                          </TableCell>
                          <TableCell sx={tableBodySx}>
                            <Typography sx={{ color: colors.neutral[600], maxWidth: 300, fontSize: 14 }} noWrap>
                              {role.description || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{getRoleTypeChip(role)}</TableCell>
                          <TableCell sx={tableBodySx}>
                            {formatDate(role.created || role.createdAt) || "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${role.permissions?.length || 0} permissions`}
                              size="small"
                              sx={{
                                fontSize: 12,
                                backgroundColor: colors.neutral[100],
                                color: colors.neutral[700],
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => handleRoleMenuOpen(e, role)}
                              aria-label="Role actions"
                            >
                              <MoreVertIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Menu
                anchorEl={roleAnchorEl}
                open={Boolean(roleAnchorEl)}
                onClose={handleRoleMenuClose}
                PaperProps={{
                  sx: {
                    borderRadius: "8px",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
                    border: `1px solid ${colors.neutral[200]}`,
                    minWidth: 150,
                  },
                }}
              >
                {isCustomRole(selectedRole) && (
                  <MenuItem
                    disabled={!canManageUsers}
                    onClick={handleEditRole}
                    sx={ { fontSize: 13, color: colors.neutral[800] }}
                  >
                    <EditIcon style={{ width: 16, height: 16, marginRight: 8, color: colors.neutral[600] }} />
                    Edit
                  </MenuItem>
                )}
                <MenuItem
                  disabled={!canManageUsers}
                  onClick={handleCloneRole}
                  sx={ { fontSize: 13, color: colors.neutral[800] }}
                >
                  <CopyIcon sx={{ width: 16, height: 16, mr: 1, color: colors.neutral[600] }} />
                  Clone
                </MenuItem>
                {isCustomRole(selectedRole) && (
                  <MenuItem
                    disabled={!canManageUsers}
                    onClick={handleDeleteRoleClick}
                    sx={ { fontSize: 13, color: colors.red[100] }}
                  >
                    <DeleteIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                    Delete
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Stack>
      </Box>

      <AddEditUserModal
        open={addEditModalOpen}
        onClose={() => {
          setUserSaveError("");
          setAddEditModalOpen(false);
        }}
        user={selectedUser}
        roleOptions={allRoleOptions}
        projects={projects || []}
        onSave={handleSaveUser}
        saveError={userSaveError}
        onClearSaveError={() => setUserSaveError("")}
      />

      <DeleteConfirmModal
        open={deleteModalOpen && Boolean(selectedUser)}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          handleConfirmDelete(selectedUser);
          setDeleteModalOpen(false);
        }}
        title="Delete User"
      >
        Are you sure you want to delete{" "}
        <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong> ({selectedUser?.email})? This action cannot be undone.
      </DeleteConfirmModal>

      <ToggleUserStatusModal
        open={toggleStatusModalOpen}
        onClose={() => setToggleStatusModalOpen(false)}
        user={selectedUser}
        onConfirm={handleToggleEnabled}
      />

      <RoleModal
        open={roleModalOpen}
        onClose={() => {
          setRoleSaveError("");
          clearError();
          setRoleModalOpen(false);
        }}
        role={selectedRole}
        onSave={handleSaveRole}
        loading={rolesLoading}
        saveError={roleSaveError}
        onClearSaveError={() => setRoleSaveError("")}
      />

      <CloneRoleModal
        open={cloneModalOpen}
        onClose={() => setCloneModalOpen(false)}
        role={selectedRole}
        onClone={handleCloneSubmit}
        loading={rolesLoading}
      />

      <DeleteRoleModal
        open={deleteRoleModalOpen}
        onClose={() => setDeleteRoleModalOpen(false)}
        role={selectedRole}
        onDelete={handleDeleteRoleSubmit}
        loading={rolesLoading}
      />

    </>
    </PermissionGate>
  );
};

export default UserManagement;
